import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import { ActionMenu } from '../../ActionMenu';
import { AvailabilityBadge } from '../../AvailabilityBadge';
import { Box } from '../../Box';
import { Icon } from '../../Icon';
import {
  ScrollingCarousel,
  ScrollingCarouselStep,
} from '../../ScrollingCarousel';
import {
  actionMenuStyles,
  badgeBottomRightStyles,
  badgeTopLeftStyles,
  heartIconStyles,
  imageBoxStyles,
  imageContainerStyles,
  imageGradientStyles,
  imageStyles,
  overlayStyles,
} from '../PackageCard.styles';
import type {
  PackageCardAvailabilityBadge,
  PackageCardImageComponent,
  PackageCardImageProps,
  PackageCardOrientation,
  PackageCardProps,
} from '../PackageCard.types';

type PackageCardMediaProps = {
  actions?: PackageCardProps['actions'];
  availabilityBadges?: PackageCardAvailabilityBadge[];
  carouselId: string;
  carouselLabel: string;
  imageComponent?: PackageCardImageComponent;
  imageHeight?: string;
  imageSizes?: string;
  images: string[];
  isFavorited?: boolean;
  onFavoriteClick?: () => void;
  orientation: PackageCardOrientation;
};

const NativePackageCardImage = ({
  alt,
  decoding,
  loading,
  sizes,
  src,
  style,
}: PackageCardImageProps) => (
  <img
    alt={alt}
    decoding={decoding}
    loading={loading}
    sizes={sizes}
    src={src}
    style={style}
  />
);

const packageCardImageStyles: React.CSSProperties = {
  height: '100%',
  inset: 0,
  objectFit: 'cover',
  objectPosition: 'center',
  position: 'absolute',
  width: '100%',
};

const PackageCardMedia = ({
  actions,
  availabilityBadges,
  carouselId,
  carouselLabel,
  imageComponent: ImageComponent = NativePackageCardImage,
  imageHeight,
  imageSizes,
  images,
  isFavorited = false,
  onFavoriteClick,
  orientation,
}: PackageCardMediaProps) => {
  const uniqueImages = useMemo(
    () => Array.from(new Set(images.filter(Boolean))),
    [images]
  );
  const [mediaRef, mediaIsNearViewport] = useInView({
    rootMargin: '600px 0px',
    triggerOnce: true,
    skip: uniqueImages.length <= 1,
  });
  const visibleImages = mediaIsNearViewport
    ? uniqueImages
    : uniqueImages.slice(0, 1);
  const shouldShowOverlay = availabilityBadges?.some(
    (badge) => badge.showOverlay
  );

  return (
    <Box
      ref={mediaRef}
      position="relative"
      overflow="hidden"
      borderRadius="var(--spacing-4)"
      css={imageContainerStyles(orientation, imageHeight)}
    >
      {availabilityBadges?.map((badge, index) => (
        <AvailabilityBadge
          key={`availability-${index}`}
          variant={badge.variant}
          css={
            badge.position === 'bottom-right'
              ? badgeBottomRightStyles
              : badgeTopLeftStyles
          }
        >
          {badge.text}
        </AvailabilityBadge>
      ))}

      {shouldShowOverlay && <Box css={overlayStyles} />}

      {visibleImages.length > 0 ? (
        <ScrollingCarousel
          ariaLabel={carouselLabel}
          showDots={visibleImages.length > 1}
          showNavigationOnHover
          id={carouselId}
        >
          {visibleImages.map((image, index) => (
            <ScrollingCarouselStep
              ariaLabel={`Image ${index + 1} of ${uniqueImages.length}`}
              key={image}
              parentId={carouselId}
            >
              <Box
                width="100%"
                css={[imageStyles, imageBoxStyles(orientation, imageHeight)]}
              >
                <ImageComponent
                  alt={`${carouselLabel}, image ${index + 1}`}
                  decoding="async"
                  loading="lazy"
                  sizes={imageSizes}
                  src={image}
                  style={packageCardImageStyles}
                />
                <Box aria-hidden="true" css={imageGradientStyles} />
              </Box>
            </ScrollingCarouselStep>
          ))}
        </ScrollingCarousel>
      ) : (
        <Box
          aria-hidden="true"
          width="100%"
          css={[imageStyles, imageBoxStyles(orientation, imageHeight)]}
        />
      )}

      {onFavoriteClick && (
        <Box
          css={heartIconStyles}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onFavoriteClick();
          }}
        >
          {isFavorited ? (
            <Icon
              variant="HeartSolid"
              size="small"
              fill="var(--color-error-500)"
            />
          ) : (
            <Icon variant="Heart" size="small" fill="var(--color-base-black)" />
          )}
        </Box>
      )}

      {actions && actions.length > 0 && orientation === 'vertical' && (
        <Box css={actionMenuStyles}>
          <ActionMenu actions={actions} />
        </Box>
      )}
    </Box>
  );
};

export default PackageCardMedia;
